import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Plug, UserRound, X } from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx, formatDate, initials } from "../../utils";
import Card from "../../components/ui/Card";
import EmptyState from "../../components/ui/EmptyState";
import ListFilters from "../../components/ui/ListFilters";
import Pagination from "../../components/ui/Pagination";
import Skeleton from "../../components/ui/Skeleton";
import { useToast } from "../../components/ui/Toast";
import useListQuery, { useSearchInput } from "../../hook/useListQuery";
import {
  AppStack,
  CopyId,
  DetailsState,
  Drawer,
  DrawerLoading,
  InfoGrid,
  InstallList,
  Pill,
  Section,
  ago,
  useOpenParam,
} from "../../components/directory/shared";

/**
 * The people who installed our apps — usually an agency admin, sometimes a
 * location's own user.
 */

const DEFAULTS = { page: 1, limit: 20, companyId: "", roleType: "", details: "", sort: "newest", q: "" };

const userLabel = (u) => u?.name || u?.email || `User ${u?.userId || ""}`.trim();
const roleText = (u) => [u?.roleType === "agency" ? "Agency" : u?.roleType === "account" ? "Location" : null, u?.role].filter(Boolean).join(" · ");

const Avatar = ({ user, size = "h-8 w-8 text-[11px]" }) => (
  <span className={cx("grid shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-500 to-violet-600 font-semibold text-white", size)}>
    {user?.name ? initials(user.name) : <UserRound size={14} />}
  </span>
);

const UserDrawer = ({ id, onClose }) => {
  const { directory } = useAppServices();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setData(null);
    setError("");
    directory.User(id).then(({ response, error: err }) => {
      if (!alive) return;
      if (err || !response) setError(err?.message || "Could not load this user.");
      else setData(response);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const u = data?.user;

  return (
    <Drawer
      onClose={onClose}
      label="User details"
      header={
        u ? (
          <div className="flex items-start gap-3">
            <Avatar user={u} size="h-11 w-11 text-[13px]" />
            <div className="min-w-0">
              <h2 className="truncate text-[16px] font-semibold tracking-tight text-ink-900">{userLabel(u)}</h2>
              {roleText(u) && <p className="truncate text-[12.5px] capitalize text-ink-500">{roleText(u)}</p>}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <DetailsState record={u} />
              </div>
            </div>
          </div>
        ) : (
          <Skeleton className="h-11 w-48" />
        )
      }
    >
      {error ? (
        <p className="m-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">{error}</p>
      ) : !data ? (
        <DrawerLoading />
      ) : (
        <>
          <InfoGrid
            rows={[
              ["User ID", <CopyId value={u.userId} />],
              ["Email", u.email && <a href={`mailto:${u.email}`} className="text-brand-600 hover:underline">{u.email}</a>],
              ["Phone", u.phone],
              ["Role", roleText(u)],
              [
                "Company",
                data.company ? (
                  <Link to={`/companies?open=${data.company._id}`} className="font-medium text-brand-600 hover:underline">
                    {data.company.name || data.company.companyId}
                  </Link>
                ) : (
                  u.companyId && <CopyId value={u.companyId} />
                ),
              ],
              ["First seen", formatDate(u.createdAt)],
              ["Last attempt", !u.lastSyncedAt && u.syncError ? u.syncError : null],
            ]}
          />
          <Section title={`Installs · ${data.installs.length}`} icon={Plug}>
            <InstallList installs={data.installs} />
          </Section>
        </>
      )}
    </Drawer>
  );
};

const Users = () => {
  const { directory } = useAppServices();
  const toast = useToast();

  const [query, setQuery] = useListQuery(DEFAULTS);
  const [search, setSearch] = useSearchInput(query.q, (q) => setQuery({ q }));
  const [openId, setOpenId] = useOpenParam();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, limit: DEFAULTS.limit });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: query.page, limit: query.limit, sort: query.sort });
    for (const key of ["companyId", "roleType", "details", "q"]) if (query[key]) params.set(key, query[key]);
    const { response, error } = await directory.Users(params.toString());
    if (error) toast(error?.message || error?.error || "Could not load users", "error");
    const next = response && Array.isArray(response.items) ? response : { items: [], total: 0, page: 1, pages: 1, limit: query.limit };
    if (next.items.length === 0 && next.total > 0 && query.page > next.pages) setQuery({ page: next.pages });
    setData(next);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.limit, query.companyId, query.roleType, query.details, query.sort, query.q]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = Boolean(query.q || query.companyId || query.roleType || query.details);
  const close = useCallback(() => setOpenId(null), [setOpenId]);
  const companyName = data.items.find((u) => u.companyId === query.companyId)?.companyName;

  return (
    <div className="space-y-5">
      {query.companyId && (
        <div className="flex items-center gap-2 text-[13px] text-ink-600">
          <Building2 size={14} className="text-ink-400" />
          Showing users of <span className="font-semibold text-ink-900">{companyName || query.companyId}</span>
          <button type="button" onClick={() => setQuery({ companyId: "" })} className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] font-medium text-ink-500 hover:bg-ink-100 hover:text-ink-800">
            <X size={12} /> Show all
          </button>
        </div>
      )}

      <Card padded={false}>
        <ListFilters
          search={search}
          onSearch={setSearch}
          placeholder="Search name, email, phone or ID"
          filters={[
            { label: "Role", value: query.roleType, onChange: (roleType) => setQuery({ roleType }), options: [["", "Any role"], ["agency", "Agency users"], ["account", "Location users"]], className: "w-40" },
            { label: "Details", value: query.details, onChange: (details) => setQuery({ details }), options: [["", "Any details"], ["synced", "Details fetched"], ["pending", "Details pending"]], className: "w-40" },
            { label: "Sort", value: query.sort === "newest" ? "" : query.sort, onChange: (sort) => setQuery({ sort: sort || "newest" }), options: [["", "Newest first"], ["name", "Name"]], className: "w-36" },
          ]}
          onClear={() => setQuery({ q: "", companyId: "", roleType: "", details: "" })}
        />

        {loading ? (
          <div className="divide-y divide-ink-150">
            {[0, 1, 2, 3].map((k) => (
              <div key={k} className="flex items-center gap-3 px-5 py-4">
                <Skeleton className="h-8 w-8" rounded="rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-1/4" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={<UserRound size={24} />}
            title={filtered ? "No users match" : "No users yet"}
            description={filtered ? "Try another role, filter or search term." : "Users appear here as people install your apps."}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-b border-ink-150 text-left">
                  {["User", "Role", "Company", "Installs", "Last install"].map((h) => (
                    <th key={h} className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-150">
                {data.items.map((u) => (
                  <tr key={u._id} onClick={() => setOpenId(u._id)} className={cx("cursor-pointer transition-colors hover:bg-ink-50", openId === u._id && "bg-brand-50/50")}>
                    <td className="max-w-[18rem] px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar user={u} />
                        <div className="min-w-0">
                          <button type="button" onClick={() => setOpenId(u._id)} className="block max-w-full truncate text-left font-medium text-ink-900 hover:text-brand-600">
                            {userLabel(u)}
                          </button>
                          {u.lastSyncedAt ? <p className="truncate text-[11.5px] text-ink-400">{u.email}</p> : <DetailsState record={u} />}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-5 py-3">
                      {roleText(u) ? <Pill tone={u.roleType === "agency" ? "violet" : "ink"}>{roleText(u)}</Pill> : <span className="text-ink-300">—</span>}
                    </td>
                    <td className="max-w-[12rem] px-5 py-3" onClick={(e) => e.stopPropagation()}>
                      {u.companyId ? (
                        <button type="button" onClick={() => setQuery({ companyId: u.companyId })} className="block max-w-full truncate text-left text-[13px] text-ink-700 hover:text-brand-600" title="Show this company's users">
                          {u.companyName || u.companyId}
                        </button>
                      ) : (
                        <span className="text-ink-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="tabular-nums text-ink-700">{u.installCount}</span>
                        <AppStack apps={u.apps} />
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-[12.5px] text-ink-500">{u.lastInstallAt ? ago(u.lastInstallAt) : <span className="text-ink-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            limit={data.limit}
            noun={data.total === 1 ? "user" : "users"}
            onPage={(page) => setQuery({ page })}
            onLimit={(limit) => setQuery({ limit })}
          />
        )}
      </Card>

      {openId && <UserDrawer id={openId} onClose={close} />}
    </div>
  );
};

export default Users;
